import { useEffect, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import api from "../lib/api";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function NotificationsBell({ userId }) {
    const [items, setItems] = useState([]);

    const load = async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data);
        } catch (_) {}
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    const unread = items.filter((n) => !(n.read_by || []).includes(userId)).length;

    const markAll = async () => {
        for (const n of items) {
            if (!(n.read_by || []).includes(userId)) {
                try { await api.post(`/notifications/${n.id}/read`); } catch (_) {}
            }
        }
        await load();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="relative h-10 w-10 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="notifications-bell">
                    <Bell size={18} className="text-onego-navy" />
                    {unread > 0 && (
                        <span className="absolute top-1.5 right-1.5 h-4 min-w-[16px] px-1 rounded-full og-gradient text-[10px] text-white font-bold flex items-center justify-center" data-testid="notifications-count">
                            {unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-[60vh] overflow-auto" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-2">
                    <DropdownMenuLabel className="font-display">Notifications</DropdownMenuLabel>
                    <button onClick={markAll} className="text-xs font-semibold text-pink-600 hover:underline flex items-center gap-1" data-testid="notifications-mark-all-read">
                        <CheckCheck size={12} /> Mark all read
                    </button>
                </div>
                <DropdownMenuSeparator />
                {items.length === 0 && (
                    <p className="px-3 py-6 text-sm text-slate-500 text-center" data-testid="notifications-empty">You&rsquo;re all caught up.</p>
                )}
                {items.map((n) => {
                    const read = (n.read_by || []).includes(userId);
                    return (
                        <div key={n.id} className={`px-3 py-3 ${read ? "opacity-70" : ""}`} data-testid={`notification-item-${n.id}`}>
                            <div className="flex items-start gap-3">
                                <span className={`h-2 w-2 mt-2 rounded-full ${
                                    n.kind === "success" ? "bg-emerald-500"
                                    : n.kind === "warning" ? "bg-amber-500"
                                    : n.kind === "critical" ? "bg-rose-500"
                                    : "bg-cyan-500"
                                }`} />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-onego-navy text-sm truncate">{n.title}</p>
                                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.body}</p>
                                    <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
